import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API, avatarSrc, formatCurrency, toImageSrc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { House, MapPin, Ruler, Bed, ArrowRight } from "@phosphor-icons/react";

// Public-facing property page reachable WITHOUT auth.
// Shows the listing + a limited agent card (no phone/email — user choice 1a).
export default function PublicPropertyPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let abort = false;
    fetch(`${API}/public/properties/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((d) => { if (!abort) setData(d); })
      .catch(() => { if (!abort) setError("This listing is no longer available."); });
    return () => { abort = true; };
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-zinc-400 p-6 text-center">
        <House size={32} className="text-gold mb-3" weight="duotone" />
        <div className="text-lg">{error}</div>
        <Link to="/login" className="mt-4 text-gold hover:text-gold-light text-sm">Sign in to Reagent Network</Link>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-ink-900" />;

  const p = data.property;
  const a = data.agent;
  const cover = p.images?.[0] || p.image_url;

  return (
    <div className="min-h-screen bg-ink-900 text-zinc-100" data-testid="public-property-page">
      <header className="border-b hairline px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-md bg-gold flex items-center justify-center">
            <House size={16} weight="fill" className="text-ink-900" />
          </span>
          <span className="font-display text-xl">Reagent Network</span>
        </Link>
        <Link to="/login">
          <Button variant="ghost" size="sm" className="text-gold hover:text-gold-light">Sign in →</Button>
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2" data-testid="public-status-badge">
          {p.status === "sold" ? "Sold" : (p.listing_for === "rent" ? "For Rent" : "For Sale")}
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-4" data-testid="public-property-title">{p.title}</h1>
        <div className="flex items-center gap-2 text-zinc-400 text-sm mb-6">
          <MapPin size={14} weight="duotone" className="text-gold" />
          <span>{p.location || [p.area, p.city].filter(Boolean).join(", ")}</span>
        </div>

        {cover && (
          <div className="rounded-xl overflow-hidden card-surface mb-6">
            <img src={toImageSrc(cover)} alt={p.title} className="w-full h-[300px] md:h-[440px] object-cover" />
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="card-surface rounded-xl p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{p.listing_for === "rent" ? "Monthly Rent" : "Price"}</div>
            <div className="font-mono text-3xl mt-2 text-gold" data-testid="public-property-price">{formatCurrency(p.price)}</div>
          </div>
          <div className="card-surface rounded-xl p-5 flex items-center gap-3">
            <Ruler size={20} weight="duotone" className="text-gold" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Size</div>
              <div className="font-mono text-xl mt-1">{p.size_sqft || "—"} sqft</div>
            </div>
          </div>
          <div className="card-surface rounded-xl p-5 flex items-center gap-3">
            <Bed size={20} weight="duotone" className="text-gold" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Bedrooms</div>
              <div className="font-mono text-xl mt-1">{p.bedrooms ? `${p.bedrooms} BHK` : "—"}</div>
            </div>
          </div>
        </div>

        {p.description && (
          <div className="card-surface rounded-xl p-6 mb-8">
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2">About this property</div>
            <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{p.description}</p>
          </div>
        )}

        {/* Agent block — name + city only (no contact). CTA to sign up. */}
        {a && (
          <div className="card-surface rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center gap-5" data-testid="public-agent-block">
            <img src={avatarSrc(a.avatar_url)} alt={a.name} className="h-16 w-16 rounded-full object-cover border border-gold/30" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Listed by</div>
              <div className="text-lg" data-testid="public-agent-name">{a.name}</div>
              <div className="text-sm text-zinc-500">{[a.specialty, a.area, a.city].filter(Boolean).join(" · ")}</div>
            </div>
            <Link to="/login">
              <Button className="bg-gold text-ink-900 hover:bg-gold-light gap-2" data-testid="public-contact-cta">
                Sign up to contact agent <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        )}

        <div className="text-center mt-12 text-xs text-zinc-600">
          Powered by <Link to="/login" className="text-gold hover:text-gold-light">Reagent Network</Link> — the invitation-only network for real estate professionals.
        </div>
      </main>
    </div>
  );
}
